import { useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { SkillPackageSummary } from "@/api/admin/skill-packages"

import { Plus } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"

import { DeleteSkillPackageDialog } from "./delete-skill-package-dialog"
import { SkillPackageEditorDialog } from "./skill-package-editor-dialog"
import {
  useDeleteSkillPackageMutation,
  useImportSkillPackageMutation,
  usePublishSkillPackageMutation,
  useSkillPackageCatalogQuery,
} from "../hooks/queries/skill-packages"
import { describeSkillPackageError, type SkillPackageOperation } from "../utils/skill-packages"

/** Skill packages (#3033, #3074): the catalog, and both ways in.
 *
 *  Two authoring paths meet here, and neither replaces the other. A zip in the
 *  anthropics/skills SKILL.md format still imports exactly as before — it is
 *  how a package built outside the product arrives. #3074 adds the portal's
 *  own path: create a package here, optionally let AI write the first draft,
 *  edit it over several sittings and publish it without a file ever leaving
 *  the browser. Both land in the same place: a DRAFT that publishing freezes
 *  into an immutable version. Installing onto an agent lives in the agent
 *  wizard's skills tab, unchanged. */
export function SkillPackagesManagementCard() {
  const { t } = useTranslation()
  const catalogQuery = useSkillPackageCatalogQuery()
  const importMutation = useImportSkillPackageMutation()
  const publishMutation = usePublishSkillPackageMutation()
  const deleteMutation = useDeleteSkillPackageMutation()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [importName, setImportName] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  // A SNAPSHOT, not a live catalog row. The editor re-seeds its form whenever
  // this identity changes, and a catalog refetch (every save invalidates it)
  // would otherwise throw away whatever the admin had typed since.
  const [editingPackage, setEditingPackage] = useState<SkillPackageSummary | undefined>(undefined)
  // The package awaiting delete confirmation (BUG-014): delete never runs on one click.
  const [pendingDelete, setPendingDelete] = useState<SkillPackageSummary | null>(null)

  /** Say WHY the operation was refused, in the reader's language (B1/G1). */
  const showError = (exc: unknown, operation: SkillPackageOperation) => {
    const described = describeSkillPackageError(exc, operation)
    const text = t(described.key, { defaultValue: described.defaultValue })
    setError(described.detail && described.detail !== text ? `${text} (${described.detail})` : text)
  }

  const packages = catalogQuery.data?.packages ?? []
  const platformPackages = catalogQuery.data?.platform_packages ?? []
  const busy = importMutation.isPending || publishMutation.isPending || deleteMutation.isPending

  const onPickZip = () => fileInputRef.current?.click()

  const onZipChosen = async (file: File | undefined) => {
    if (!file) return
    setError(null)
    setNotice(null)
    try {
      const buffer = await file.arrayBuffer()
      let binary = ""
      const bytes = new Uint8Array(buffer)
      const chunk = 0x8000
      for (let index = 0; index < bytes.length; index += chunk) {
        binary += String.fromCharCode(...bytes.subarray(index, index + chunk))
      }
      const imported = await importMutation.mutateAsync({
        zipBase64: btoa(binary),
        ...(importName.trim() ? { name: importName.trim() } : {}),
      })
      setImportName("")
      setNotice(
        t("adminDashboard.skillPackages.imported", {
          defaultValue:
            "Imported {{name}} as a draft. Publish it to make it installable on agents.",
          name: imported.name,
        })
      )
    } catch (exc) {
      showError(exc, "import")
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  const onPublish = async (pkg: SkillPackageSummary) => {
    setError(null)
    setNotice(null)
    try {
      const version = await publishMutation.mutateAsync({
        packageId: pkg.id,
        revision: pkg.revision,
      })
      setNotice(
        t("adminDashboard.skillPackages.published", {
          defaultValue: "Published {{name}} v{{version}}.",
          name: pkg.name,
          version: version.version,
        })
      )
    } catch (exc) {
      showError(exc, "publish")
    }
  }

  const onDelete = async (pkg: SkillPackageSummary) => {
    setError(null)
    setNotice(null)
    try {
      await deleteMutation.mutateAsync(pkg.id)
    } catch (exc) {
      showError(exc, "delete")
    } finally {
      // Close either way: a refusal (e.g. still installed on an agent) is
      // explained in the card's own error line, which the dialog would cover.
      setPendingDelete(null)
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.skillPackages.help", {
          defaultValue:
            "Skill packages specialize a generic agent: instructions, scripts, tool recommendations and eval cases in one installable unit. Import a zip in the SKILL.md format, publish a version, then install it on an agent in the agent wizard.",
        })}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          data-testid="skill-package-new"
          disabled={busy}
          onClick={() => {
            setError(null)
            setNotice(null)
            setEditingPackage(undefined)
            setEditorOpen(true)
          }}
        >
          <Plus className="size-4" />
          {t("adminDashboard.skillPackages.create", { defaultValue: "New skill package" })}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="max-w-56"
          value={importName}
          placeholder={t("adminDashboard.skillPackages.namePlaceholder", {
            defaultValue: "Name override (optional)",
          })}
          onChange={(event) => setImportName(event.target.value)}
        />
        <Button disabled={busy} onClick={onPickZip}>
          {t("adminDashboard.skillPackages.importZip", { defaultValue: "Import skill zip" })}
        </Button>
        {busy ? <Spinner className="size-4" /> : null}
        <input
          ref={fileInputRef}
          accept=".zip,application/zip"
          className="hidden"
          type="file"
          onChange={(event) => onZipChosen(event.target.files?.[0])}
        />
      </div>

      {error ? (
        <p
          className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm"
          data-testid="skill-package-error"
          role="alert"
        >
          {error}
        </p>
      ) : null}
      {notice ? <p className="text-muted-foreground text-sm">{notice}</p> : null}

      {catalogQuery.isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Spinner className="size-4" />
          {t("adminDashboard.skillPackages.loading", { defaultValue: "Loading packages…" })}
        </div>
      ) : null}
      {catalogQuery.isError ? (
        <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm">
          {t("adminDashboard.skillPackages.loadFailed", {
            defaultValue: "Could not load the skill package catalog.",
          })}
        </p>
      ) : null}

      <div className="space-y-2">
        {[...platformPackages, ...packages].map((pkg) => (
          <div
            key={pkg.id}
            className="flex items-center justify-between gap-3 rounded-md border p-3"
          >
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium">
                <span className="font-mono">{pkg.name}</span>
                {pkg.display_name && pkg.display_name !== pkg.name ? (
                  <span className="text-muted-foreground"> — {pkg.display_name}</span>
                ) : null}
                {pkg.source === "platform" ? (
                  <Badge className="ml-2" variant="secondary">
                    {t("adminDashboard.skillPackages.platformBadge", { defaultValue: "Platform" })}
                  </Badge>
                ) : null}
              </span>
              <span className="text-muted-foreground truncate text-xs">
                {pkg.latest_version > 0
                  ? t("adminDashboard.skillPackages.latestVersion", {
                      defaultValue: "Latest published: v{{version}}",
                      version: pkg.latest_version,
                    })
                  : t("adminDashboard.skillPackages.draftOnly", {
                      defaultValue: "Draft only — publish to make it installable",
                    })}
                {pkg.description ? ` · ${pkg.description}` : null}
              </span>
            </span>
            {pkg.source === "custom" ? (
              <span className="flex flex-wrap items-center gap-2">
                <Button
                  data-testid={`skill-package-edit-${pkg.name}`}
                  disabled={busy}
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setError(null)
                    setNotice(null)
                    setEditingPackage(pkg)
                    setEditorOpen(true)
                  }}
                >
                  {t("adminDashboard.skillPackages.edit", { defaultValue: "Edit" })}
                </Button>
                <Button disabled={busy} size="sm" variant="outline" onClick={() => onPublish(pkg)}>
                  {t("adminDashboard.skillPackages.publish", { defaultValue: "Publish" })}
                </Button>
                <Button
                  data-testid={`skill-package-delete-${pkg.name}`}
                  disabled={busy}
                  size="sm"
                  variant="ghost"
                  onClick={() => setPendingDelete(pkg)}
                >
                  {t("adminDashboard.skillPackages.delete", { defaultValue: "Delete" })}
                </Button>
              </span>
            ) : null}
          </div>
        ))}
        {!catalogQuery.isLoading && packages.length === 0 && platformPackages.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("adminDashboard.skillPackages.empty", {
              defaultValue:
                "No skill packages yet — create one here, with AI's help if you like, or import a zip.",
            })}
          </p>
        ) : null}
      </div>

      <DeleteSkillPackageDialog
        isPending={deleteMutation.isPending}
        pkg={pendingDelete}
        onConfirm={() => (pendingDelete ? onDelete(pendingDelete) : Promise.resolve())}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) setPendingDelete(null)
        }}
      />
      <SkillPackageEditorDialog
        isOpen={editorOpen}
        pkg={editingPackage}
        onOpenChange={setEditorOpen}
      />
    </div>
  )
}
